import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetMyChapterUpgradeApplicationsQuery,
  useChapterAcceptUpgradeApplicationMutation,
  useChapterRejectUpgradeApplicationMutation,
} from "../api/chapterApi";
import {
  ArrowUpCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";

function safeMsg(x: any) {
  return String(x?.data?.message || x?.message || x?.error || "Something went wrong");
}

const STATUS_CLS: Record<string, string> = {
  submitted: "bg-blue-100 text-blue-700 border-blue-200",
  awaiting_endorsements: "bg-indigo-100 text-indigo-700 border-indigo-200",
  under_review: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  returned: "bg-orange-100 text-orange-700 border-orange-200",
};

const TABS = [
  "all",
  "submitted",
  "awaiting_endorsements",
  "under_review",
  "returned",
  "approved",
  "rejected",
] as const;
type Tab = (typeof TABS)[number];

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });
}

export default function ChapterUpgradeApplicationsPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [flashMsg, setFlashMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [reason, setReason] = useState("");
  const [acceptTarget, setAcceptTarget] = useState<any | null>(null);
  const [comment, setComment] = useState("");
  const [actingId, setActingId] = useState<string | null>(null);

  const { data, isLoading, isFetching, refetch } =
    useGetMyChapterUpgradeApplicationsQuery(undefined);
  const [acceptApp] = useChapterAcceptUpgradeApplicationMutation();
  const [rejectApp, { isLoading: rejecting }] = useChapterRejectUpgradeApplicationMutation();

  const list: any[] = Array.isArray(data?.data) ? data!.data : [];

  // Chapters this leader ACTS for, on top of the one they hold office in. When
  // there are any, the list is no longer all one chapter's work, so each row has
  // to say which chapter it belongs to.
  const actingFor = data?.acting_for ?? [];
  const showChapter = actingFor.length > 0;

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      all: list.length,
      submitted: 0,
      awaiting_endorsements: 0,
      under_review: 0,
      returned: 0,
      approved: 0,
      rejected: 0,
    };
    for (const a of list) if (c[a.status] != null) c[a.status]++;
    return c;
  }, [list]);

  const visible = useMemo(
    () => (tab === "all" ? list : list.filter((a) => a.status === tab)),
    [list, tab],
  );

  const flash = (ok: boolean, text: string) => {
    setFlashMsg({ ok, text });
    setTimeout(() => setFlashMsg(null), 4000);
  };

  const onAccept = async () => {
    if (!acceptTarget) return;
    if (!comment.trim()) {
      flash(false, "A comment is required to endorse.");
      return;
    }
    setActingId(acceptTarget.id);
    try {
      const res: any = await acceptApp({ id: acceptTarget.id, comment: comment.trim() }).unwrap();
      flash(true, res?.message ?? "Application endorsed.");
      setAcceptTarget(null);
      setComment("");
    } catch (e) {
      flash(false, safeMsg(e));
    } finally {
      setActingId(null);
    }
  };

  const onReject = async () => {
    if (!rejectTarget) return;
    try {
      const res: any = await rejectApp({ id: rejectTarget.id, reason }).unwrap();
      flash(true, res?.message ?? "Application rejected.");
      setRejectTarget(null);
      setReason("");
    } catch (e) {
      flash(false, safeMsg(e));
    }
  };

  const busy = isLoading || isFetching;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <Link to="/chapter/referrals" className="hover:text-gray-900 transition-colors">
              Chapter Referrals
            </Link>
            <span className="opacity-30">/</span>
            <span className="text-gray-900">Upgrade Applications</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Chapter Upgrade Applications
          </h1>
          <p className="text-sm text-gray-500 font-medium">
            {data?.chapter?.name
              ? `Grade-upgrade applications from ${data.chapter.name}.`
              : "Grade-upgrade applications from your chapter."}
            {showChapter && (
              <span className="text-amber-700">
                {" "}You also act for {actingFor.map((c: any) => c.name).join(", ")}, so their
                applications appear here too.
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={busy}
          className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50 self-start"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {flashMsg && (
        <div
          className={`p-4 rounded-2xl border flex items-center gap-3 ${
            flashMsg.ok
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {flashMsg.ok ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <p className="text-sm font-bold">{flashMsg.text}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-100/50 p-1.5 rounded-[20px] flex flex-wrap gap-1">
        {TABS.map((t) => {
          const isActive = tab === t;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                isActive
                  ? "bg-white text-gray-900 shadow-sm ring-1 ring-black/5"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {t.replace("_", " ")}
              <span className="ml-1 px-1.5 py-0.5 rounded-md bg-gray-200/60 text-gray-500">
                {counts[t]}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="py-20 text-center animate-pulse text-xs font-black text-gray-400 uppercase tracking-widest">
            Loading…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-20 bg-white border-2 border-dashed border-gray-100 rounded-[40px] text-center">
            <ArrowUpCircle className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">
              No {tab === "all" ? "" : tab.replace("_", " ")} applications
            </p>
          </div>
        ) : (
          visible.map((a) => {
            const canAct =
              ["submitted", "awaiting_endorsements", "under_review", "returned"].includes(a.status) &&
              !a.chapter_accepted_at;
            const grade =
              [a.from_category?.name, a.to_category?.name].filter(Boolean).join(" → ") ||
              "Membership upgrade";
            const isActing = actingId === a.id;
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-[28px] p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase border ${
                          STATUS_CLS[a.status] ?? "bg-gray-100 text-gray-600 border-gray-200"
                        }`}
                      >
                        {String(a.status).replace("_", " ")}
                      </span>
                      {showChapter && a.chapter?.name && (
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase border bg-amber-50 text-amber-700 border-amber-200">
                          {a.chapter.name}
                        </span>
                      )}
                      {a.chapter_accepted_at && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black text-green-600">
                          <ShieldCheck className="w-3.5 h-3.5" /> Endorsed
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-black text-gray-900 capitalize">
                      {a.surname} {a.other_names}
                      <span className="text-gray-300 font-medium ml-2">({a.title || "Applicant"})</span>
                    </h3>
                    <p className="text-sm font-bold text-gray-500 flex items-center gap-2">
                      <ArrowUpCircle className="w-4 h-4 text-indigo-500" />
                      <span className="text-gray-900">{grade}</span>
                      {a.route && <span className="text-gray-400 capitalize">· {a.route} route</span>}
                    </p>
                    <p className="text-[11px] text-gray-400 font-medium">
                      Submitted {fmtDate(a.created_at)} · {a.user?.email}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Link
                      to={`/chapter/my-upgrade-applications/${a.id}`}
                      className="px-4 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-800 transition-all"
                    >
                      View details
                    </Link>
                    {canAct && (
                      <>
                        <button
                          onClick={() => {
                            setRejectTarget(a);
                            setReason("");
                          }}
                          className="px-4 py-2.5 bg-white border border-red-100 text-red-600 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-50 transition-all"
                        >
                          Reject
                        </button>
                        <button
                          onClick={() => {
                            setAcceptTarget(a);
                            setComment("");
                          }}
                          disabled={isActing || !!a.chapter_accepted_at}
                          className="px-4 py-2.5 bg-green-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-green-700 transition-all disabled:opacity-50"
                        >
                          {isActing ? "..." : a.chapter_accepted_at ? "Endorsed" : "Endorse"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Endorse modal (comment required) */}
      {acceptTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 space-y-5">
            <div>
              <h2 className="font-black text-gray-900 text-lg">Endorse upgrade application</h2>
              <p className="text-[12px] text-gray-500 font-medium mt-1">
                {acceptTarget.surname} {acceptTarget.other_names}
              </p>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Chapter comment <span className="text-red-500">*</span>
              </label>
              <textarea
                className="mt-1 w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 min-h-[100px]"
                placeholder="Your comment endorsing this candidate…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setAcceptTarget(null);
                  setComment("");
                }}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-[12px] font-black text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={onAccept}
                disabled={!comment.trim() || actingId === acceptTarget.id}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white text-[12px] font-black uppercase tracking-wider hover:bg-green-700 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {actingId === acceptTarget.id ? "Endorsing…" : "Confirm Endorse"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 space-y-5">
            <div>
              <h2 className="font-black text-gray-900 text-lg">Reject upgrade application</h2>
              <p className="text-[12px] text-gray-500 font-medium mt-1">
                {rejectTarget.surname} {rejectTarget.other_names}
              </p>
            </div>
            <textarea
              className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 min-h-[100px]"
              placeholder="Reason (optional)…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setRejectTarget(null)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-[12px] font-black text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={onReject}
                disabled={rejecting}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white text-[12px] font-black uppercase tracking-wider hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {rejecting ? "Rejecting…" : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
